import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router';
import { Eye, EyeOff, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AORHierarchyNode, AREA, SECTOR } from './utils/schema';
import { buildAorTree, collectDescendantIds } from './utils/buildAorTree';
import { useGetAorsByHierarchy } from '../../../../hooks/useGetAorsByHierarchy';
import { HierarchyItemCard } from './HierarchyItemCard';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { useAorText } from 'utils/aorTerminology';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { registerOrgWorkspaceTourApi } from '../../orgWorkspaceTour/orgWorkspaceTourBridge';
import { registerFocCopLayersTourApi } from '../../focCopLayersTour/focCopLayersTourBridge';
import { useAutoZoomToHomeAor } from './utils/useAutoZoomToHomeAor';
import { ORG_WORKSPACE_TOUR_PARAM } from '../../orgWorkspaceTour/orgWorkspaceTourSteps';

const AORs = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [focusedDistrictId, setFocusedDistrictId] = useState<string | null>(
    null
  );
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const [tourExpandKey, setTourExpandKey] = useState<string | null>(null);
  /** A card the tour holds open, and the `data-tour` its details carry. */
  const [tourOpenCard, setTourOpenCard] = useState<{
    id: string;
    tour: string;
  } | null>(null);
  /** Subunit (or card) whose View Assets list the FOC-ORG-001e tour holds open. */
  const [tourAssetsOpenId, setTourAssetsOpenId] = useState<string | null>(null);
  const [tourMapButtonId, setTourMapButtonId] = useState<string | null>(null);
  const {
    aorFilterIds,
    setAorFilterIds,
    isAorVisible,
    setIsAorVisible,
    toggleAorVisibility,
  } = useUscgMapContext();
  const { zoomToAorById } = useAutoZoomToHomeAor();
  const tourMapRef = useRef({
    zoomToAorById,
    setAorFilterIds,
    setIsAorVisible,
  });
  useEffect(() => {
    tourMapRef.current = { zoomToAorById, setAorFilterIds, setIsAorVisible };
  }, [zoomToAorById, setAorFilterIds, setIsAorVisible]);
  const aorText = useAorText();

  const { data: aors = [], isLoading } = useGetAorsByHierarchy();
  const [searchParams, setSearchParams] = useSearchParams();
  const focRequirements = useUscgFocRequirementsFlag();
  const tourStartedRef = useRef(false);

  const { childrenByParent, nodesById } = useMemo(
    () => buildAorTree(aors),
    [aors]
  );

  const allAorIds = useMemo(() => aors.map((aor) => aor.id), [aors]);

  const isAllVisible = useMemo(
    () => isAorVisible && aorFilterIds === null,
    [aorFilterIds, isAorVisible]
  );

  const handleToggleAll = useCallback(() => {
    if (isAllVisible) {
      setAorFilterIds([]);
      toggleAorVisibility();
      return;
    }

    if (!isAorVisible) {
      toggleAorVisibility();
    }

    setAorFilterIds(null);
  }, [isAllVisible, isAorVisible, setAorFilterIds, toggleAorVisibility]);

  const areas = useMemo(
    () => aors.filter((aor) => aor.levelName === AREA),
    [aors]
  );

  /**
   * The card list starts at the AORs sitting directly under an Area, or under
   * nothing at all. For Coast Guard data that is exactly the districts; for an
   * organization that never defined hierarchy levels it is whatever the user
   * created without a parent.
   */
  const topLevelNodes = useMemo(
    () =>
      aors.filter((aor) => {
        if (aor.levelName === AREA) return false;
        const parent = aor.parentItemId ? nodesById[aor.parentItemId] : null;
        return !parent || parent.levelName === AREA;
      }),
    [aors, nodesById]
  );

  const treeRef = useRef({ aors, topLevelNodes, childrenByParent });
  treeRef.current = { aors, topLevelNodes, childrenByParent };

  useEffect(() => {
    const kids = (parentId: string) =>
      treeRef.current.childrenByParent[parentId] ?? [];
    const firstArea = () =>
      treeRef.current.aors.find((aor) => aor.levelName === AREA) ?? null;
    const districtWithSubunits = () => {
      for (const district of treeRef.current.topLevelNodes) {
        const sector = kids(district.id).find(
          (child) => child.levelName === SECTOR && kids(child.id).length > 0
        );
        if (sector) return { district, sector };
      }
      return null;
    };

    const firstDistrictIn = (areaId: string | null) =>
      treeRef.current.topLevelNodes.find(
        (node) => !areaId || node.parentItemId === areaId
      ) ?? null;
    const withDescendants = (id: string) => [
      id,
      ...collectDescendantIds(treeRef.current.childrenByParent, id),
    ];
    const zoomTo = (id: string) => {
      void tourMapRef.current
        .zoomToAorById(id, {
          filterIds: withDescendants(id),
          ensureVisible: true,
        })
        .catch(() => {});
    };
    const reset = () => {
      setSearchText('');
      setSelectedAreaId(null);
      setFocusedDistrictId(null);
      setSelectedSectorId(null);
      setTourExpandKey(null);
      setTourOpenCard(null);
      setTourAssetsOpenId(null);
      setTourMapButtonId(null);
    };

    return registerOrgWorkspaceTourApi({
      showOverview: reset,
      expandFirstDistrict: () => {
        const district = treeRef.current.topLevelNodes[0] ?? null;
        reset();
        setTourExpandKey(district ? `aor-${district.id}` : null);
      },
      revealSectorSubunits: () => {
        const match = districtWithSubunits();
        reset();
        setTourExpandKey(match ? `aor-${match.district.id}` : null);
      },
      openSectorSubunits: () => {
        const match = districtWithSubunits();
        reset();
        if (!match) return;
        setFocusedDistrictId(match.district.id);
        setSelectedSectorId(match.sector.id);
      },
      showAreaDetails: () => {
        const area = firstArea();
        reset();
        setSelectedAreaId(area?.id ?? null);
      },
      showDistrictDetails: () => {
        const area = firstArea();
        const district = firstDistrictIn(area?.id ?? null);
        reset();
        setSelectedAreaId(area?.id ?? null);
        setFocusedDistrictId(district?.id ?? null);
        setTourExpandKey(district ? `aor-${district.id}` : null);
      },
      showSectorDetails: () => {
        const match = districtWithSubunits();
        const district = match?.district ?? treeRef.current.topLevelNodes[0];
        const sector =
          match?.sector ??
          (district
            ? kids(district.id).find((child) => child.levelName === SECTOR)
            : undefined);
        reset();
        if (!district) return;
        setTourExpandKey(`aor-${district.id}`);
        if (sector) {
          setTourOpenCard({ id: sector.id, tour: 'aor-sector-details' });
        }
      },
      showSubunitDetails: () => {
        const match = districtWithSubunits();
        reset();
        if (!match) return;
        const subunit = kids(match.sector.id)[0];
        setFocusedDistrictId(match.district.id);
        setSelectedSectorId(match.sector.id);
        if (subunit) {
          setTourOpenCard({ id: subunit.id, tour: 'aor-subunit-details' });
        }
      },
      showOwnershipAssets: () => {
        const match = districtWithSubunits();
        reset();
        if (!match) return;
        const subunit = kids(match.sector.id)[0];
        setFocusedDistrictId(match.district.id);
        setSelectedSectorId(match.sector.id);
        if (subunit) {
          setTourOpenCard({ id: subunit.id, tour: 'aor-subunit-details' });
          setTourAssetsOpenId(subunit.id);
        }
      },
      showAreaOnMap: () => {
        const area = firstArea();
        const district = firstDistrictIn(area?.id ?? null);
        reset();
        setSelectedAreaId(area?.id ?? null);
        setTourMapButtonId(district?.id ?? null);
        if (area) zoomTo(area.id);
      },
      zoomToFirstDistrict: () => {
        const area = firstArea();
        const district = firstDistrictIn(area?.id ?? null);
        reset();
        setSelectedAreaId(area?.id ?? null);
        if (district) zoomTo(district.id);
      },
      showAllAreasOnMap: () => {
        reset();
        tourMapRef.current.setIsAorVisible(true);
        tourMapRef.current.setAorFilterIds(null);
      },
      firstAreaId: () =>
        firstArea()?.id ?? treeRef.current.topLevelNodes[0]?.id ?? null,
    });
  }, []);

  useEffect(() => {
    const kids = (parentId: string) =>
      treeRef.current.childrenByParent[parentId] ?? [];
    const firstArea = () =>
      treeRef.current.aors.find((aor) => aor.levelName === AREA) ?? null;
    const districtWithSubunits = () => {
      for (const district of treeRef.current.topLevelNodes) {
        const sector = kids(district.id).find(
          (child) => child.levelName === SECTOR && kids(child.id).length > 0
        );
        if (sector) return { district, sector };
      }
      return null;
    };
    const firstDistrictIn = (areaId: string | null) =>
      treeRef.current.topLevelNodes.find(
        (node) => !areaId || node.parentItemId === areaId
      ) ?? null;
    const reset = () => {
      setSearchText('');
      setSelectedAreaId(null);
      setFocusedDistrictId(null);
      setSelectedSectorId(null);
      setTourExpandKey(null);
      setTourOpenCard(null);
      setTourAssetsOpenId(null);
      setTourMapButtonId(null);
    };

    return registerFocCopLayersTourApi({
      showAorsOverview: reset,
      expandFirstDistrict: () => {
        const district = treeRef.current.topLevelNodes[0] ?? null;
        reset();
        setTourExpandKey(district ? `aor-${district.id}` : null);
      },
      revealSector: () => {
        const match = districtWithSubunits();
        reset();
        setTourExpandKey(match ? `aor-${match.district.id}` : null);
      },
      showSectorDetails: () => {
        const match = districtWithSubunits();
        const district = match?.district ?? treeRef.current.topLevelNodes[0];
        const sector =
          match?.sector ??
          (district
            ? kids(district.id).find((child) => child.levelName === SECTOR)
            : undefined);
        reset();
        if (!district) return;
        setTourExpandKey(`aor-${district.id}`);
        if (sector) {
          setTourOpenCard({ id: sector.id, tour: 'aor-sector-details' });
        }
      },
      showDistrictDetails: () => {
        const area = firstArea();
        const district = firstDistrictIn(area?.id ?? null);
        reset();
        setSelectedAreaId(area?.id ?? null);
        setFocusedDistrictId(district?.id ?? null);
        setTourExpandKey(district ? `aor-${district.id}` : null);
      },
      showBreadcrumbs: () => {
        const match = districtWithSubunits();
        reset();
        if (!match) return;
        setFocusedDistrictId(match.district.id);
        setSelectedSectorId(match.sector.id);
      },
      showAreaFilters: () => {
        const area = firstArea();
        reset();
        setSelectedAreaId(area?.id ?? null);
      },
    });
  }, []);

  const wantsOrgWorkspaceTour =
    searchParams.get(ORG_WORKSPACE_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!wantsOrgWorkspaceTour) {
      tourStartedRef.current = false;
      return;
    }
    if (!focRequirements || isLoading || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(ORG_WORKSPACE_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../orgWorkspaceTour/orgWorkspaceTour')
        .then(({ startOrgWorkspaceTour }) => {
          startOrgWorkspaceTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsOrgWorkspaceTour, focRequirements, isLoading, setSearchParams]);

  /**
   * While searching, keep every name match on screen along with its ancestors
   * and descendants.
   */
  const searchVisibleIds = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query.length) return null;

    const visible = new Set<string>();
    aors.forEach((aor) => {
      if (!aor.name.toLowerCase().includes(query)) return;

      visible.add(aor.id);
      collectDescendantIds(childrenByParent, aor.id).forEach((id) =>
        visible.add(id)
      );

      let parentId = aor.parentItemId;
      while (parentId && nodesById[parentId] && !visible.has(parentId)) {
        visible.add(parentId);
        parentId = nodesById[parentId].parentItemId;
      }
    });

    return visible;
  }, [aors, childrenByParent, nodesById, searchText]);

  const filteredTopLevelNodes = useMemo(() => {
    if (focusedDistrictId) {
      return topLevelNodes.filter((node) => node.id === focusedDistrictId);
    }

    let result = topLevelNodes;

    if (selectedAreaId) {
      result = result.filter((node) => node.parentItemId === selectedAreaId);
    }

    if (searchVisibleIds) {
      result = result.filter((node) => searchVisibleIds.has(node.id));
    }

    return result;
  }, [topLevelNodes, focusedDistrictId, selectedAreaId, searchVisibleIds]);

  const childrenOf = useCallback(
    (parentId: string) => {
      const children = childrenByParent[parentId] ?? [];
      if (!searchVisibleIds) return children;
      return children.filter((child) => searchVisibleIds.has(child.id));
    },
    [childrenByParent, searchVisibleIds]
  );

  const activeDistrict = focusedDistrictId
    ? (nodesById[focusedDistrictId] ?? null)
    : null;
  const activeSector = selectedSectorId
    ? (nodesById[selectedSectorId] ?? null)
    : null;

  const activeChips = useMemo(() => {
    if (!activeDistrict) {
      return [];
    }

    const chips = [
      {
        filterName: 'District',
        filterValue: activeDistrict.name,
        onRemove: () => {
          setSelectedSectorId(null);
          setFocusedDistrictId(null);
        },
      },
    ];

    if (selectedSectorId && activeSector) {
      chips.push({
        filterName: 'Sector',
        filterValue: activeSector.name,
        onRemove: () => {
          setSelectedSectorId(null);
        },
      });
    }

    return chips;
  }, [activeDistrict, activeSector, selectedSectorId]);

  // Mutually recursive renderers, so function declarations rather than consts: AOR nesting has no fixed depth.
  function renderCard(node: AORHierarchyNode) {
    const sectionKey = `aor-${node.id}`;
    const expanded = tourExpandKey === sectionKey;
    const tourOpen = tourOpenCard?.id === node.id;
    return (
      <HierarchyItemCard
        key={node.id}
        allAorIds={allAorIds}
        level={node.levelName ?? undefined}
        descendantIds={collectDescendantIds(childrenByParent, node.id)}
        item={node}
        sectionKey={sectionKey}
        cardTour={expanded ? 'aor-district-card' : undefined}
        detailsTour={
          tourOpen
            ? tourOpenCard.tour
            : expanded && focusedDistrictId === node.id
              ? 'aor-district-details'
              : undefined
        }
        mapTour={tourMapButtonId === node.id ? 'aor-map-button' : undefined}
        forceOpen={expanded || tourOpen}
        forceAssetsOpen={tourAssetsOpenId === node.id}
      >
        {renderChildCards(node.id)}
      </HierarchyItemCard>
    );
  }

  /**
   * Sectors keep the Coast Guard drill-down: their subunits open as their own
   * list instead of nesting inline.
   */
  function renderSectorCard(
    sector: AORHierarchyNode,
    parentId: string,
    markSubunits: boolean
  ) {
    const hasSubUnits = childrenOf(sector.id).length > 0;
    const tourOpen = tourOpenCard?.id === sector.id;

    return (
      <HierarchyItemCard
        key={sector.id}
        allAorIds={allAorIds}
        level={SECTOR}
        descendantIds={collectDescendantIds(childrenByParent, sector.id)}
        item={sector}
        sectionKey={`sector-${sector.id}`}
        detailsTour={tourOpen ? tourOpenCard.tour : undefined}
        forceOpen={tourOpen}
        forceAssetsOpen={tourAssetsOpenId === sector.id}
        onAction={
          hasSubUnits
            ? () => {
                setFocusedDistrictId(parentId);
                setSelectedSectorId(sector.id);
              }
            : undefined
        }
        actionLabel={hasSubUnits ? 'Show Sector SubUnits' : undefined}
        actionTour={markSubunits ? 'aor-sector-subunits' : undefined}
        noActionMessage={!hasSubUnits ? 'No SubUnits' : undefined}
      />
    );
  }

  function renderChildCards(parentId: string): React.ReactNode {
    const children = childrenOf(parentId);
    if (!children.length) return null;

    let markedSubunits = false;
    return (
      <div>
        {children.map((child) => {
          if (child.levelName !== SECTOR) return renderCard(child);
          const hasSubUnits = childrenOf(child.id).length > 0;
          const mark =
            hasSubUnits &&
            !markedSubunits &&
            tourExpandKey === `aor-${parentId}`;
          if (mark) markedSubunits = true;
          return renderSectorCard(child, parentId, mark);
        })}
      </div>
    );
  }

  return (
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:bg-background tw:text-foreground"
      data-tour="aor-workspace"
    >
      <div className="tw:sticky tw:top-0 tw:z-10 tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-border tw:bg-background tw:pb-2">
        <div className="tw:flex tw:items-center tw:gap-2">
          <div className="tw:relative tw:min-w-0 tw:flex-1">
            <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search..."
              className="tw:h-8 tw:pl-8 tw:text-xs"
              aria-label="Search..."
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            data-tour="aor-map-all"
            title={
              isAllVisible
                ? aorText('Hide all AORs from map')
                : aorText('Show all AORs on map')
            }
            aria-label={
              isAllVisible ? aorText('Hide all AORs') : aorText('Show all AORs')
            }
            onClick={handleToggleAll}
          >
            {isAllVisible ? (
              <Eye className="tw:h-4 tw:w-4" />
            ) : (
              <EyeOff className="tw:h-4 tw:w-4" />
            )}
          </Button>
        </div>
        {focusedDistrictId && activeDistrict ? (
          <div
            className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5"
            data-tour="aor-hierarchy-chips"
          >
            {activeChips.map((chip) => (
              <Badge
                key={`${chip.filterName}-${chip.filterValue}`}
                variant="outline"
                className="tw:h-6 tw:gap-1 tw:pr-1"
              >
                <span className="tw:max-w-[12rem] tw:truncate">
                  {chip.filterName} is {chip.filterValue}
                </span>
                <button
                  type="button"
                  className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                  aria-label={`Remove ${chip.filterName} filter`}
                  onClick={chip.onRemove}
                >
                  <X className="tw:size-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          areas.length > 0 && (
            <div
              className="tw:flex tw:flex-wrap tw:gap-1.5"
              data-tour="aor-area-filters"
            >
              <Button
                type="button"
                size="sm"
                variant={selectedAreaId === null ? 'default' : 'outline'}
                onClick={() => setSelectedAreaId(null)}
              >
                All
              </Button>
              {areas.map((area) => (
                <Button
                  key={area.id}
                  type="button"
                  size="sm"
                  variant={selectedAreaId === area.id ? 'default' : 'outline'}
                  data-tour={
                    selectedAreaId === area.id ? 'aor-area-selected' : undefined
                  }
                  onClick={() => setSelectedAreaId(area.id)}
                >
                  {area.name}
                </Button>
              ))}
            </div>
          )
        )}
      </div>
      <div
        className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:space-y-2 tw:overflow-x-hidden tw:overflow-y-auto"
        data-tour="aor-hierarchy"
      >
        {isLoading ? (
          <div className="tw:py-4 tw:text-center tw:text-xs tw:text-muted-foreground">
            {aorText('Loading AORs...')}
          </div>
        ) : selectedSectorId ? (
          <>
            {childrenOf(selectedSectorId).map((subUnit) => renderCard(subUnit))}
          </>
        ) : (
          filteredTopLevelNodes.map((node) => renderCard(node))
        )}
      </div>
    </div>
  );
};

export default AORs;
